import { useMemo } from 'react';

import type { HarnessAgentName, PlanStage, PlanState } from '@aline/common';
import { flattenPlanNodes, getIsWriterArtifactMissing, InternalError } from '@aline/common';
import * as Sentry from '@sentry/react';

import {
  buildNodeMarkdown,
  getVisibleNodesInOrder,
} from '../../../chat-harness/useHarnessFlattenedText';
import type { NodeStatus } from '../../../chat-harness/useHarnessFlattenedText';
import type { ChatAssistantMessage } from '../../../chat-types';

export interface SubAgentRow {
  agent: HarnessAgentName;
  completedAt: string | null;
  error: string | null;
  nodeId: number;
  nodeText: string;
  /**
   * Stage id (1-based) of the stage this node lives in. Stable across
   * a plan's lifetime. Lets the renderer group parallel siblings into
   * a single card list and inline single-stage nodes directly in chat.
   */
  stageId: number;
  /** Whether this node is alone in its stage (single) or has siblings (parallel). */
  stageType: PlanStage['type'];
  startedAt: string | null;
  status: NodeStatus;
  /**
   * Planner-emitted user-facing title ("Search SEC rules"). Null on
   * pre-title plans; the card falls back to the humanised agent slug.
   */
  title: string | null;
}

/**
 * Derives one `SubAgentRow` per touched plan node, in start order, for
 * the sub-agent cards UI. Each row carries the per-node markdown slice
 * so the card body can render via `ChatPanelMarkdown` and pick up the
 * existing tool-call / thinking-block elements.
 */
export function useSubAgentRows(
  message: ChatAssistantMessage,
  planState: PlanState
): { rows: SubAgentRow[] } {
  const rows = useMemo(() => {
    if (!planState.plan) return [];

    /**
     * Build a node-id → (stageId, stageType) lookup once per plan so
     * every row can carry its stage metadata without re-walking stages.
     */
    const stageInfoByNodeId = new Map<number, { stageId: number; stageType: PlanStage['type'] }>();
    for (const { node, stageId, stageType } of flattenPlanNodes(planState.plan)) {
      stageInfoByNodeId.set(node.id, { stageId, stageType });
    }

    return getVisibleNodesInOrder(planState).map(node => {
      const nodeState = planState.nodeStates[String(node.id)] ?? null;
      const { body, status } = buildNodeMarkdown({
        node,
        planState,
      });
      /**
       * A writer activity can finish "successfully" while still failing
       * to produce its structured artifact (e.g. editor returns an
       * apology message because upstream anchors were missing). Surface
       * those runs as FAILED so the card renders the error icon
       * instead of the green check — the body still expands to show
       * the apology prose for the user to read.
       */
      const effectiveStatus =
        status === 'completed' && getIsWriterArtifactMissing(node.agent, body) ? 'failed' : status;
      const stageInfo = stageInfoByNodeId.get(node.id);
      if (!stageInfo) {
        /**
         * `flattenPlanNodes` walks every node in `planState.plan`, and
         * `getVisibleNodesInOrder` only returns nodes drawn from that
         * same plan, so the lookup is total by construction. A miss
         * means the plan/planState contract broke (e.g. a node was
         * pulled from a different plan, or planState.plan was swapped
         * mid-render). Surface loudly instead of silently coercing
         * `stageId` to `0` and `stageType` to `'single'`.
         */
        Sentry.captureException(
          new InternalError(
            `SubAgentRow stageInfo missing for nodeId=${node.id} in message ${message.id}`
          ),
          { tags: { component: 'chat-harness-plan-tree' } }
        );
        throw new InternalError(
          `SubAgentRow stageInfo missing for nodeId=${node.id}; plan/planState contract violation.`
        );
      }
      return {
        agent: node.agent,
        completedAt: nodeState?.completedAt ?? null,
        error: nodeState?.error ?? null,
        nodeId: node.id,
        nodeText: body,
        stageId: stageInfo.stageId,
        stageType: stageInfo.stageType,
        startedAt: nodeState?.startedAt ?? null,
        status: effectiveStatus,
        title: node.title ?? null,
      };
    });
  }, [planState, message.id]);

  return { rows };
}
